import {
  Box,
  Button,
  Card,
  CardContent,
  Container,
  Grid,
  Typography,
  useTheme,
} from "@mui/material";
import React from "react";
import { useNavigate } from "react-router-dom";
import { categoriesData } from "../../../../helpers/Arrays";
import DownloadIcon from "@mui/icons-material/Download";
import { Images } from "../../../../helpers/Images";

const RCCodeTemplate = () => {
  const navigate = useNavigate();
  const theme = useTheme();

  const categories = [
    {
      title: "Scratch",
      description:
        "Scratch programming category challenges participants to create interactive stories, games, and animations using visual block-based programming.",
      details: [
        "Individual Competition",
        "Timed Programming Tasks",
        "Creative Projects",
        "Skill-based Evaluation",
      ],
      pdfLink: "https://drive.google.com/uc?export=download&id=136pheWmaNgW2uFWd33U776JECZ78a3iD",
    },
    {
      title: "Python",
      description:
        "Python programming category tests participants' logic and problem-solving skills through advanced programming challenges and projects.",
      details: [
        "Individual Competition",
        "Timed Programming Tasks",
        "Algorithm Challenges",
        "Skill-based Evaluation",
      ],
      pdfLink: "https://drive.google.com/uc?export=download&id=1_eoGrOMH0zuqi7TqtFr3qmnJQZZ2IFPM",
    },
    {
      title: "C++",
      description:
        "C++ programming category challenges participants with complex programming tasks requiring advanced coding skills and efficient problem-solving.",
      details: [
        "Individual Competition",
        "Timed Programming Tasks",
        "Advanced Challenges",
        "Skill-based Evaluation",
      ],
      pdfLink: "https://drive.google.com/uc?export=download&id=1UQ1oUpnZzct3g6eQRuu4C7ekTHqDBO3q",
    },
    {
      title: "Creative Code",
      description:
        "Creative Code category encourages participants to showcase their creativity through innovative programming projects and unique solutions.",
      details: [
        "1/2 Participants in a team",
        "Creative Projects",
        "Innovation Focus",
        "Skill-based Evaluation",
      ],
      pdfLink: "https://drive.google.com/uc?export=download&id=1Ro3PLrO2DQKUKTdVc5K3Xjb6lHFkoJcW",
    },
  ];

  return (
    <>
      <Box
        sx={{
          backgroundImage: `linear-gradient(0deg, rgba(0, 0, 0, 0.7), rgba(0, 0, 0, 0.5)), url(${Images.rccode})`,
          backgroundSize: "cover",
          backgroundPosition: "center",
          backgroundRepeat: "no-repeat",
          color: "white",
          py: 8,
          mb: 6,
          position: "relative",
          "&::before": {
            content: '""',
            position: "absolute",
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: "rgba(0, 0, 0, 0.5)",
            zIndex: 1,
          },
        }}
      >
        <Container maxWidth="lg" sx={{ position: "relative", zIndex: 2 }}>
          <Typography
            variant="h1"
            sx={{
              fontSize: { xs: "2.5rem", md: "3.5rem" },
              fontWeight: "700",
              textAlign: "center",
              mb: 2,
              textShadow: "2px 2px 4px rgba(0, 0, 0, 0.5)",
            }}
          >
            RC.CODE Competition
          </Typography>
          <Typography
            variant="body1"
            sx={{
              textAlign: "center",
              maxWidth: "900px",
              mx: "auto",
              opacity: 0.85,
              textShadow: "1px 1px 2px rgba(0, 0, 0, 0.5)",
              fontSize: { xs: "0.9rem", md: "1rem" },
            }}
          >
            The competition challenges students' logic, problem-solving, and
            creativity through timed programming tasks and projects.
            Participants compete individually under strict rules ensuring
            originality, fairness, and skill-based evaluation, with top
            performers qualifying for international recognition.
          </Typography>
        </Container>
      </Box>

      <Container maxWidth="lg" sx={{ mb: 8 }}>
        <Grid container spacing={4}>
          {categories.map((category, index) => (
            <Grid item xs={12} md={6} key={index}>
              <Card
                sx={{
                  height: "100%",
                  display: "flex",
                  flexDirection: "column",
                  transition: "transform 0.2s",
                  "&:hover": {
                    transform: "translateY(-8px)",
                    boxShadow: theme.shadows[8],
                  },
                }}
              >
                <CardContent sx={{ flexGrow: 1 }}>
                  <Typography
                    variant="h4"
                    sx={{
                      fontWeight: "600",
                      mb: 2,
                      color: theme.palette.primary.main,
                    }}
                  >
                    {category.title}
                  </Typography>

                  <Typography
                    variant="body1"
                    sx={{ mb: 3, color: "text.secondary" }}
                  >
                    {category.description}
                  </Typography>

                  <Box sx={{ mb: 3 }}>
                    {category.details.map((detail, idx) => (
                      <Typography
                        key={idx}
                        variant="body2"
                        sx={{
                          display: "flex",
                          alignItems: "center",
                          mb: 1,
                          "&:before": {
                            content: '"•"',
                            mr: 1,
                            color: theme.palette.primary.main,
                          },
                        }}
                      >
                        {detail}
                      </Typography>
                    ))}
                  </Box>

                  <Box sx={{ display: "flex", gap: 2, flexWrap: "wrap" }}>
                    <Button
                      variant="contained"
                      startIcon={<DownloadIcon />}
                      component="a"
                      href={category.pdfLink}
                      download
                      sx={{
                        backgroundColor: theme.palette.primary.main,
                        "&:hover": {
                          backgroundColor: theme.palette.primary.dark,
                        },
                      }}
                    >
                      Rules PDF
                    </Button>
                    <Button
                      variant="outlined"
                      onClick={() => navigate("/firststage")}
                      sx={{
                        borderColor: theme.palette.primary.main,
                        color: theme.palette.primary.main,
                        "&:hover": {
                          borderColor: theme.palette.primary.dark,
                          backgroundColor: "rgba(0, 0, 0, 0.04)",
                        },
                      }}
                    >
                      Register Now
                    </Button>
                  </Box>
                </CardContent>
              </Card>
            </Grid>
          ))}
        </Grid>
      </Container>

      <Box
        sx={{
          width: "100%",
          backgroundColor: "primary.main",
        }}
      >
        <Box
          sx={{
            width: "100%",
            padding: { xs: "50px 20px", sm: "50px 60px", lg: "50px 120px" },
            maxWidth: "1500px",
            margin: "auto",
          }}
        >
          <Typography
            variant={"h2"}
            sx={{
              textAlign: "center",
              fontSize: { xs: "40px" },
              fontWeight: "500",
              color: "secondary.main",
            }}
          >
            Our All Categories
          </Typography>
          <Box
            sx={{
              height: "3px",
              maxWidth: { xs: "100px", md: "150px" },
              width: "100%",
              backgroundColor: "secondary.main",
              margin: "10px auto 0 auto",
            }}
          />
          <Grid
            container
            spacing={2}
            sx={{ marginTop: "50px", justifyContent: "center" }}
          >
            {categoriesData
              ?.filter((category) => category.title !== "RC Code")
              ?.map((item) => (
                <Grid item xs={12} sm={6} md={3} key={item.title}>
                  <Box
                    sx={{
                      backgroundImage: `linear-gradient(0deg, rgba(0, 0, 0, 0.7), rgba(0, 0, 0, 0.5)), url(${item.image})`,
                      backgroundSize: "cover",
                      backgroundPosition: "center",
                      backgroundRepeat: "no-repeat",
                      display: "flex",
                      flexDirection: "column",
                      justifyContent: "flex-end",
                      width: "100%",
                      minHeight: { xs: "200px", sm: "250px" },
                      borderRadius: "10px",
                    }}
                  >
                    <Typography
                      variant={"h3"}
                      sx={{
                        textAlign: "center",
                        fontSize: "35px",
                        fontWeight: "500",
                        color: "secondary.main",
                      }}
                    >
                      {item.title}
                    </Typography>
                    <Box
                      sx={{
                        height: "3px",
                        maxWidth: "90px",
                        width: "100%",
                        backgroundColor: "secondary.main",
                        margin: "5px auto 5px auto",
                      }}
                    />
                    <Box
                      sx={{
                        width: "100%",
                        display: "flex",
                        justifyContent: "center",
                      }}
                    >
                      <Button
                        variant="contained"
                        color="secondary"
                        sx={{
                          fontSize: "13px",
                          fontWeight: "600",
                          color: "primary.main",
                          width: "fit-content",
                          margin: { xs: "5px 0 20px 0", sm: "10px 0 40px 0" },
                        }}
                        onClick={() => navigate(item.link)}
                      >
                        view
                      </Button>
                    </Box>
                  </Box>
                </Grid>
              ))}
          </Grid>
        </Box>
      </Box>
    </>
  );
};

export default RCCodeTemplate;
